import axios from "axios";

// Admin logins expire after 8 hours (backend: controllers/adminController.js).
// When that happens the server answers "Invalid Token" (or 401) — instead of
// showing that raw message, send the admin back to the login page with a clear
// "your session expired" note.

export const getToken = () => sessionStorage.getItem("adminToken") || "";

// true if the token is missing, unreadable or past its expiry time
export const isTokenExpired = (token = getToken()) => {
  if (!token) return true;
  try {
    const { exp } = JSON.parse(atob(token.split(".")[1]));
    return typeof exp === "number" && exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

let redirecting = false;
export const logoutExpired = () => {
  if (redirecting) return;
  redirecting = true;
  sessionStorage.clear();
  window.location.href = "/admin/login?expired=1";
};

const sentWithLogin = (headers) => {
  if (!headers) return false;
  const get = (k) => (typeof headers.get === "function" ? headers.get(k) : headers[k] || headers[k.toLowerCase()]);
  const auth = get("Authorization") || get("authorization");
  return typeof auth === "string" && auth.startsWith("Bearer ");
};
const isExpiredAnswer = (status, message) =>
  status === 401 || (status === 400 && message === "Invalid Token");

// Our server's address (same rule as App.js; read here to avoid a circular import)
const apiBase = () =>
  (process.env.NODE_ENV === "development" ? process.env.REACT_APP_BACKEND_URL : process.env.REACT_APP_BACKEND_URL_PROD) || "";
const isOurServer = (url) => {
  const u = typeof url === "string" ? url : url?.url || "";
  const base = apiBase();
  return (base && u.startsWith(base)) || u.startsWith("/");
};
// The server now requires the admin login on every admin action, so attach it
// to every request to our server (unless the page already set one).
const withLogin = (init = {}) => {
  const token = getToken();
  if (!token) return init;
  const headers = new Headers(init.headers || {});
  if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
  return { ...init, headers };
};

let installed = false;
// Watch every request the panel makes (fetch + axios); only requests that
// carried the admin login are checked, so the login page itself is never affected.
export const installSessionGuard = () => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const realFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    if (isOurServer(input)) init = withLogin(init);
    const res = await realFetch(input, init);
    if ((res.status === 401 || res.status === 400) && sentWithLogin(init.headers)) {
      let message = "";
      try { message = (await res.clone().json()).message; } catch { /* not JSON */ }
      if (isExpiredAnswer(res.status, message)) logoutExpired();
    }
    return res;
  };

  axios.interceptors.request.use((config) => {
    const token = getToken();
    if (token && isOurServer(config.url || "")) {
      config.headers = config.headers || {};
      if (typeof config.headers.set === "function") {
        if (!config.headers.has("Authorization")) config.headers.set("Authorization", `Bearer ${token}`);
      } else if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  axios.interceptors.response.use(undefined, (error) => {
    const r = error?.response;
    if (r && sentWithLogin(error.config?.headers) && isExpiredAnswer(r.status, r.data?.message)) logoutExpired();
    return Promise.reject(error);
  });

  // also catch a login that runs out while the panel sits open
  setInterval(() => {
    if (sessionStorage.getItem("adminToken") && isTokenExpired()) logoutExpired();
  }, 60 * 1000);
};
